import { JOURNAL_VIEW_SEND_ERROR } from '@/entities/journal/lib/journalMessages';
import {
  AUTHOR_ARTICLES_LOAD_ERROR,
  SIMILAR_ARTICLES_LOAD_ERROR,
} from '@/pages/journalArticle/lib/journalArticleMessages';
import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import { ROOT_JOURNAL_URL } from '@/shared/constants/urlConstants';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';

import { useJournalTopics } from '@/entities/journal/model/useJournalTopics';
import { joinWordsWithLimit } from '@/pages/journalArticle/lib/joinWordsWithLimit';
import { buildAuthorInfo } from '@/pages/journalArticle/lib/buildAuthorInfo';
import { buildArticleSeoMeta } from '@/pages/journalArticle/lib/buildArticleSeoMeta';
import { buildBreadcrumbData } from '@/shared/utils/buildSchemaData';
import {
  AUTHOR_ARTICLES_PER_PAGE_DESKTOP,
  AUTHOR_ARTICLES_PER_PAGE_MOBILE,
  SIMILAR_ARTICLES_PER_PAGE,
} from '@/pages/journalArticle/lib/useJournalArticleConstants';

import type { IJournalArticle } from '@/entities/journal/model/types';
import type { ILawyerCardInfo } from '@/entities/lawyer/@x/journal';
import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';
import type { ArticleMeta } from '@/pages/journalArticle/lib/buildArticleSeoMeta';
import type { BreadcrumbSchemaData } from '@/shared/utils/buildSchemaData';
import { useErrorHandler } from '@/shared/lib/errors';

const useJournalArticle = () => {
  const { handleError } = useErrorHandler();
  const route = useRoute();
  const isMobile: boolean = useIsMobile();

  const { $journalService } = useNuxtApp();
  const { journalTopicsDropdownItems } = useJournalTopics(
    computed(() => String(route.params.theme || '')),
  );

  const articleSlug: ComputedRef<string> = computed(() =>
    String(route.params.slug || ''),
  );
  const url: ComputedRef<string> = computed(() => `${ROOT_URL_HOST}${route.path}`);
  const authorArticlesPerPage = computed(() =>
    isMobile ? AUTHOR_ARTICLES_PER_PAGE_MOBILE : AUTHOR_ARTICLES_PER_PAGE_DESKTOP,
  );

  const getAuthorArticles = async (
    userId: number,
    ignoreId: number,
    page: number,
  ): Promise<{ articles: IJournalArticle[]; total: number }> => {
    const response = await $journalService.getArticles<IJournalArticle[]>({
      user_id: userId,
      ignore_id: ignoreId,
      per_page: authorArticlesPerPage.value,
      page,
      'sort-created_at': 'desc',
    });
    const articles = response.data ?? [];
    return { articles, total: response.meta?.total ?? articles.length };
  };

  const pageAsyncData = useAsyncData(`journal-article:${articleSlug.value}`, async () => {
    const response = await $journalService.getArticleBySlug(articleSlug.value);
    const article = response.data ?? null;

    if (!article?.id) {
      throw createError({ statusCode: 404, statusMessage: 'Статья не найдена' });
    }

    return article;
  });

  const { data, status, error } = pageAsyncData;

  watchEffect(() => {
    if (error.value) {
      showError(createError({ statusCode: getErrorStatus(error.value), fatal: true }));
    }
  });

  const article: ComputedRef<IJournalArticle | null> = computed(() => data.value ?? null);
  const isShowSkeleton: ComputedRef<boolean> = computed(() => status.value === 'pending');

  const topicName: ComputedRef<string> = computed(() => article.value?.topic?.name ?? '');
  const journalDetailLink: ComputedRef<string> = computed(
    () => `${ROOT_JOURNAL_URL}${article.value?.topic?.slug}/`,
  );
  const lawyerInfo: ComputedRef<ILawyerCardInfo | null> = computed(
    () => article.value?.user?.lawyer ?? null,
  );
  const articleAuthorId: ComputedRef<number | undefined> = computed(
    () => article.value?.user?.id ?? article.value?.user_id,
  );
  const articleTopicId: ComputedRef<number | undefined> = computed(
    () => article.value?.topic_id,
  );

  const meta: ComputedRef<ArticleMeta> = computed(() =>
    buildArticleSeoMeta(article.value),
  );

  const authorName: ComputedRef<string> = computed(
    () => buildAuthorInfo(lawyerInfo.value, article.value?.user ?? null)?.fullName ?? '',
  );

  const breadcrumbSchemaData: ComputedRef<BreadcrumbSchemaData> = computed(() => {
    if (!article.value) return buildBreadcrumbData([]);
    const items = [
      { name: 'Главная', url: '/' },
      { name: 'Журнал', url: ROOT_JOURNAL_URL },
    ];
    if (article.value.topic?.slug && topicName.value) {
      items.push({ name: topicName.value, url: journalDetailLink.value });
    }
    items.push({ name: article.value.content?.title ?? '', url: route.path });
    return buildBreadcrumbData(items);
  });

  const breadcrumbs: ComputedRef<Breadcrumb[]> = computed(() => {
    if (!article.value) return [];

    const articleTitle = article.value.content?.title ?? '';

    return [
      { id: 1, text: 'Главная', to: '/' },
      { id: 2, text: 'Журнал', to: ROOT_JOURNAL_URL },
      {
        id: 3,
        text: topicName.value,
        to: journalDetailLink.value,
        dropdownItems: journalTopicsDropdownItems.value,
        dropdownTitle: 'Выберите рубрику',
      },
      {
        id: 4,
        text: joinWordsWithLimit(articleTitle.split(' ')),
        fullText: articleTitle,
      },
    ];
  });

  const authorArticles: Ref<IJournalArticle[]> = ref([]);
  const authorArticlesTotal: Ref<number> = ref(0);
  const authorArticlesPage: Ref<number> = ref(1);
  const isAuthorArticlesRequested: Ref<boolean> = ref(false);

  const isShowLoadMore: ComputedRef<boolean> = computed(
    () => authorArticles.value.length < authorArticlesTotal.value,
  );

  const loadAuthorArticles = async (): Promise<void> => {
    if (!articleAuthorId.value || !article.value) return;

    try {
      const { articles, total } = await getAuthorArticles(
        articleAuthorId.value,
        article.value.id,
        authorArticlesPage.value,
      );
      authorArticles.value = [...authorArticles.value, ...articles];
      authorArticlesTotal.value = total;
    } catch (loadError) {
      handleError(loadError, {
        context: { action: AUTHOR_ARTICLES_LOAD_ERROR },
      });
    }
  };

  const loadMoreAuthorArticles = (): void => {
    authorArticlesPage.value += 1;
    loadAuthorArticles();
  };

  const similarArticles: Ref<IJournalArticle[]> = ref([]);

  const getSimilarArticles = async (): Promise<void> => {
    if (!article.value?.topic_id) return;

    try {
      const response = await $journalService.getArticles<IJournalArticle[]>({
        per_page: SIMILAR_ARTICLES_PER_PAGE,
        page: 1,
        by_group: 1,
        topic_id: article.value.topic_id,
        ignore_id: article.value.id,
        'sort-published_at': 'desc',
      });
      similarArticles.value = Array.isArray(response.data) ? response.data : [];
    } catch (loadError) {
      handleError(loadError, {
        context: { action: SIMILAR_ARTICLES_LOAD_ERROR },
      });
    }
  };

  const countPageView = async (): Promise<void> => {
    try {
      await $journalService.sendArticleView(articleSlug.value);
    } catch (viewError) {
      handleError(viewError, {
        context: { action: JOURNAL_VIEW_SEND_ERROR },
      });
    }
  };

  const requestAuthorArticlesOnce = (): void => {
    if (isAuthorArticlesRequested.value || !article.value) return;
    isAuthorArticlesRequested.value = true;
    loadAuthorArticles();
  };

  watch(article, requestAuthorArticlesOnce);

  onMounted(() => {
    countPageView();
    requestAuthorArticlesOnce();
  });

  useSeoMeta({
    title: () => meta.value.title,
    description: () => meta.value.description,
    keywords: () => article.value?.seo_keywords,
    ogTitle: () => meta.value.title,
    ogDescription: () => meta.value.description,
    ogSiteName: 'Сервис по подбору юристов, адвокатов - Юрик.ру',
    ogType: 'article',
    ogUrl: () => url.value,
    ogLocale: 'ru',
    ogImage: () => meta.value.image,
    articlePublishedTime: () => article.value?.published_at,
    articleModifiedTime: () => article.value?.updated_at,
    articleSection: () => topicName.value,
    articleAuthor: () => (authorName.value ? [authorName.value] : []),
    twitterCard: 'summary_large_image',
    twitterTitle: () => meta.value.title,
    twitterDescription: () => meta.value.description,
    twitterImage: () => meta.value.image,
    robots: 'all',
  });

  useHead({
    meta: [{ property: 'vk:image', content: () => meta.value.image }],
  });

  return {
    pageReady: pageAsyncData,
    pageError: error,
    isMobile,
    article,
    isShowSkeleton,
    breadcrumbs,
    breadcrumbSchemaData,
    lawyerInfo,
    articleAuthorId,
    articleTopicId,
    topicName,
    journalDetailLink,
    authorArticles,
    isShowLoadMore,
    loadMoreAuthorArticles,
    similarArticles,
    getSimilarArticles,
  };
};

export default useJournalArticle;
